/**
 * Site multicast (IGMP snooping) -- server-state hooks and response types.
 *
 * Backend: api/v2/multicast.py (/sites/{id}/multicast, .../plan, .../apply). This is the REAL
 * per-site IGMP-snooping surface that replaced the FRR/PIM demo. Kept in its own module so its
 * types do not bloat hooks.ts; keys come from the shared scope-prefixed queryKeys.
 *
 * Propose-then-confirm, exactly like the VLAN rollout: the plan is READ-ONLY and returns ONE
 * plan_token over every switch; apply sends only that token and the server re-plans from live
 * data, refusing if anything moved. Intent writes and apply are msp_admin server-side.
 *
 * EVERY ecCLI line in a plan is UNVERIFIED on our hardware (services/switch_multicast) -- the
 * preview shows the exact commands so they can be checked on a spare VLAN before Apply.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import { qk } from "./queryKeys";
import { useSession } from "@/stores/session";
import type { SwitchCliAccess, SwitchCliVia, SwitchCliRejection } from "@/lib/switchApplyResult";

/* ---- types ------------------------------------------------------------------------------ */

/** One VLAN's IGMP-snooping intent (models/site_multicast.SiteMulticastSetting). */
export interface MulticastSetting {
  site_id: number;
  vlan_id: number;
  /** joined from the site VLAN when it is a defined VLAN; null for an ad-hoc media VLAN. */
  vlan_name: string | null;
  snooping: boolean;
  /** Run OUR querier on this VLAN. Default false -- leave off when an upstream headend queries. */
  querier: boolean;
  immediate_leave: boolean;
  version: number | null;
  mrouter_ports: string[];
  updated_by: string | null;
  updated_at: string | null;
}

/** The body PUT /sites/{id}/multicast accepts (vlan_id required; the rest optional partial). */
export interface MulticastSettingInput {
  vlan_id: number;
  snooping?: boolean;
  querier?: boolean;
  immediate_leave?: boolean;
  version?: number | null;
  mrouter_ports?: string[];
}

/** A candidate VLAN to offer in the picker (the site's defined VLANs). */
export interface MulticastVlanOption {
  vlan_id: number;
  vlan_name: string;
}

/** Per-VLAN live state parsed off one switch (values null = unknown, never a false "off"). */
export interface MulticastVlanState {
  vlan: number;
  /** CONFIGURED: `ip igmp snooping vlan N` is present. */
  snooping: boolean | null;
  /**
   * RUNNING: the switch reports the VLAN's snooping as Active.
   *
   * Separate from `snooping` on purpose. A VLAN can be configured Enabled and still be
   * Inactive -- VLANs 41/42/43 are Enabled on both Jedi Office switches but Active only on
   * the core. Showing only the configured value calls a dormant VLAN "on".
   */
  running?: boolean | null;
  querier: boolean | null;
  version: number | null;
}

/** A routed SVI on the switch (`show ip interface`). */
export interface SwitchL3Interface {
  vlan: number;
  admin_up: boolean;
  link_up: boolean;
  address_mode: string | null;
  ip: string | null;
  mask: string | null;
  prefix: number | null;
}

/** One route (`show ip route`). `selected` is the ">" flag, `fib` the "*". */
export interface SwitchRoute {
  code: string;
  prefix: string;
  via: string | null;
  interface: string | null;
  vlan: number | null;
  distance: number | null;
  metric: number | null;
  selected: boolean;
  fib: boolean;
}

/** PIM state. `supported: false` means the switch REJECTED the command -- it has no PIM at
 *  all -- which is a different fact from `enabled: false`, and drives the greyed-out
 *  controls rather than hiding them. */
export interface SwitchPimState {
  supported: boolean;
  enabled: boolean | null;
  interfaces: { vlan: number; up: boolean; mode: string | null; active: boolean;
                ip: string | null }[];
}

/** Live multicast forwarding entries (`show ip mroute`) -- what is actually flowing. */
export interface SwitchMrouteState {
  supported: boolean;
  forwarding: boolean | null;
  entries: {
    /** "*" for a shared-tree (*,G) entry, rendered as-is. */
    source: string;
    group: string;
    uptime: string | null;
    owner: string | null;
    incoming_vlan: number | null;
    rpf_neighbor: string | null;
    outgoing: { vlan: number; state: string }[];
  }[];
}

/** What this switch can actually do, PROBED from what it answered -- never inferred from a
 *  model string. */
export interface SwitchCapability {
  l3: boolean;
  pim: boolean;
  routed_vlans: number[];
}

/** One switch's live IGMP-snooping read-back (services/site_multicast.state). */
export interface MulticastSwitchState {
  serial: string;
  name: string;
  /** observed | not_observed | cli_error | error. not_observed = we could not parse the output. */
  status: string;
  error?: string | null;
  via?: SwitchCliVia | null;
  access?: SwitchCliAccess | null;
  global_snooping?: boolean | null;
  vlans?: MulticastVlanState[];
  groups?: { vlan: number; group: string }[];
  mrouter?: { vlan: number; port: string; kind: string }[];
  /* ---- L3 + multicast routing, added 2026-09-25 ------------------------------------
   * Optional because a switch that could not be reached returns none of it, and because
   * an older controller will not send it at all.
   * --------------------------------------------------------------------------------- */
  capability?: SwitchCapability;
  interfaces?: SwitchL3Interface[];
  routes?: SwitchRoute[];
  pim?: SwitchPimState;
  mroute?: SwitchMrouteState;
}

export interface MulticastState {
  site_id: number;
  vlans: number[];
  switches: MulticastSwitchState[];
  error?: string;
}

export interface MulticastIntent {
  settings: MulticastSetting[];
  site_vlans: MulticastVlanOption[];
  state?: MulticastState;
}

/** A refusal (blocks apply) or a warning (does not). */
export interface MulticastItem {
  code: string;
  message: string;
  serial: string | null;
}

/** One switch in a plan: the exact ecCLI + the access path. */
export interface MulticastPlanSwitch {
  serial: string;
  name: string;
  model: string | null;
  host: string | null;
  online: boolean;
  reachability: string | null;
  access: SwitchCliAccess | null;
  commands: string[];
  vlans: number[];
  refusals: MulticastItem[];
  warnings: MulticastItem[];
}

export interface MulticastPlan {
  site_id: number;
  site_name: string;
  plan_token: string;
  can_apply: boolean;
  vlans: { vlan: number; version: number | null; querier: boolean }[];
  switches: MulticastPlanSwitch[];
  refusals: MulticastItem[];
  warnings: MulticastItem[];
}

/** One switch's apply result (services/site_multicast.apply). */
export interface MulticastApplySwitch {
  serial: string;
  name: string;
  commands: string[];
  ok: boolean;
  saved?: boolean | null;
  error?: string | null;
  stage?: string | null;
  partial?: boolean;
  rejected?: SwitchCliRejection[];
  via?: SwitchCliVia | null;
  access?: SwitchCliAccess | null;
  status?: string;
  verification?: unknown;
}

export interface MulticastApplyResult {
  site_id: number;
  ok: boolean;
  plan_token: string;
  switches: MulticastApplySwitch[];
  applied: string[];
  failed: string | null;
}

/* ---- hooks ------------------------------------------------------------------------------- */

function useScope() {
  return useSession((s) => s.currentOrgId);
}

/** GET /sites/{id}/multicast -- the per-VLAN intent + candidate VLANs (fast; no device contact). */
export function useMulticastIntent(siteId: string) {
  const orgId = useScope();
  return useQuery({
    queryKey: qk.sites.multicast(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<MulticastIntent>(`/sites/${siteId}/multicast`, undefined, signal),
    enabled: !!orgId && !!siteId,
  });
}

/** GET /sites/{id}/multicast?state=true -- LIVE read-back (opens `show`-only CLI sessions). */
export function useMulticastState(siteId: string, enabled: boolean) {
  const orgId = useScope();
  return useQuery({
    queryKey: qk.sites.multicastState(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<MulticastIntent>(`/sites/${siteId}/multicast`, { state: true }, signal),
    enabled: enabled && !!orgId && !!siteId,
    // Live device data: never serve a stale read as if it were current.
    staleTime: 0,
    retry: false,
  });
}

/** PUT /sites/{id}/multicast -- set ONE VLAN's intent (msp_admin). */
export function useSaveMulticastSetting(siteId: string) {
  const qc = useQueryClient();
  const orgId = useScope();
  return useMutation({
    mutationFn: (body: MulticastSettingInput) =>
      api.put<MulticastIntent>(`/sites/${siteId}/multicast`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.sites.multicast(orgId, siteId) });
      void qc.invalidateQueries({ queryKey: qk.sites.multicastPlan(orgId, siteId) });
    },
  });
}

/** DELETE /sites/{id}/multicast/{vlan_id} -- forget a VLAN's intent (msp_admin). */
export function useDeleteMulticastSetting(siteId: string) {
  const qc = useQueryClient();
  const orgId = useScope();
  return useMutation({
    mutationFn: (vlanId: number) =>
      api.del<{ settings: MulticastSetting[] }>(`/sites/${siteId}/multicast/${vlanId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.sites.multicast(orgId, siteId) });
      void qc.invalidateQueries({ queryKey: qk.sites.multicastPlan(orgId, siteId) });
    },
  });
}

/** POST /sites/{id}/multicast/plan -- READ-ONLY plan (exact ecCLI + plan_token). Fetched on demand. */
export function useMulticastPlan(siteId: string, enabled: boolean) {
  const orgId = useScope();
  return useQuery({
    queryKey: qk.sites.multicastPlan(orgId, siteId),
    queryFn: () => api.post<MulticastPlan>(`/sites/${siteId}/multicast/plan`),
    enabled: enabled && !!orgId && !!siteId,
    staleTime: 0,
    retry: false,
  });
}

/** POST /sites/{id}/multicast/apply {plan_token} -- apply (msp_admin). */
export function useApplyMulticast(siteId: string) {
  const qc = useQueryClient();
  const orgId = useScope();
  return useMutation({
    mutationFn: (planToken: string) =>
      api.post<MulticastApplyResult>(`/sites/${siteId}/multicast/apply`, { plan_token: planToken }),
    onSuccess: () => {
      // Applying changes what the switches see: refresh the live-state section.
      void qc.invalidateQueries({ queryKey: qk.sites.multicastState(orgId, siteId) });
      void qc.invalidateQueries({ queryKey: qk.sites.multicastPlan(orgId, siteId) });
    },
  });
}
